//Global imports
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface faq {
    id: string;
    question: string;
    answer: string;
    image: string | null;
    fileName?: string;
    height?: number;
    width?: number;
    fileSizeInKb?: number;
}

interface faqsState {
    faqs: faq[];
    faqIdWithNote: string | null;
}

const initialState: faqsState = {
    faqs: [{ id: '1', question: '', answer: '', image: null }],
    faqIdWithNote: null,
};

const faqsSlice = createSlice({
    name: 'faqs',
    initialState,
    reducers: {
        // Add new FAQ
        addFaq: (state, action: PayloadAction<faq>) => {
            state.faqs.push(action.payload);
        },

        // Update FAQ content like question and answer
        updateFaqContent: (state, action: PayloadAction<faq>) => {
            const { id, question, answer } = action.payload;
            const newFaqs = state.faqs.map((faq) => {
                if (faq.id === id) {
                    return { ...faq, question, answer };
                }
                return faq;
            });
            state.faqs = newFaqs;
        },

        // Remove FAQ by ID
        removeFaq: (state, action: PayloadAction<string>) => {
            const id = action.payload;
            const newFaqs = state.faqs.filter((item, i) => item.id !== id);
            state.faqs =
                newFaqs.length > 0
                    ? newFaqs
                    : [{ id: '1', question: '', answer: '', image: null }];
        },

        // Manage FAQ position after drag and drop
        manageFaqPosition: (state, action: PayloadAction<faq[]>) => {
            state.faqs = action.payload;
        },

        // Add image in FAQ by ID
        addImageInFaq: (
            state,
            action: PayloadAction<{
                id: string;
                image: string;
                fileName: string;
                height: number;
                width: number;
                fileSizeInKb: number;
            }>,
        ) => {
            const { id, image, fileName, height, width, fileSizeInKb } =
                action.payload;
            const newFaqs = state.faqs.map((faq) => {
                if (faq.id === id) {
                    return {
                        ...faq,
                        image,
                        fileName,
                        height,
                        width,
                        fileSizeInKb,
                    };
                }
                return faq;
            });
            state.faqs = newFaqs;
        },

        // Remove image in FAQ by ID
        removeImageInFaq: (
            state,
            action: PayloadAction<{
                id: string;
            }>,
        ) => {
            const { id } = action.payload;
            const deleteFaqData = state.faqs.find((faq) => faq.id === id);
            if (deleteFaqData) {
                const newFaqs = state.faqs.map((faq) => {
                    if (faq.id === id) {
                        return {
                            id: deleteFaqData?.id,
                            question: deleteFaqData?.question,
                            answer: deleteFaqData?.answer,
                            image: null,
                        };
                    }
                    return faq;
                });
                state.faqs = newFaqs;
            }
        },

        setInitialFaq: (state, action: PayloadAction) => {
            state.faqs = [{ id: '1', question: '', answer: '', image: null }];
        },

        updateFaqIdWithNote: (state, action: PayloadAction<string | null>) => {
            state.faqIdWithNote = action.payload;
        },
    },
});

export const {
    addFaq,
    updateFaqContent,
    removeFaq,
    manageFaqPosition,
    addImageInFaq,
    removeImageInFaq,
    setInitialFaq,
    updateFaqIdWithNote,
} = faqsSlice.actions;

export default faqsSlice.reducer;
